import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../services/api/endpoints";
import { useAuth } from "../auth/context";
import { entitlementsKey } from "../catalogue/queries";
import type { TierId } from "../../services/api/schemas";

type Plan = { priceId: string; tier: Exclude<TierId, "free"> };

/**
 * What to say, and specifically whether to suggest retrying.
 *
 * A 409 from the upgrade endpoint is a DECISION, not a wobble — the same request
 * gets the same answer forever. Offering "try again in a moment" against one is
 * the kind of message that has someone clicking a button twenty times before
 * they contact support, and by then they are already annoyed.
 */
function messageFor(e: unknown): string {
  const err = e as { status?: number; message?: string };
  if (err?.status === 409) {
    if (err.message === "upgrade_unavailable") {
      /* A plan set up by support: the entitlement row is real, the Stripe
         subscription behind it is not, so there is no price to swap. */
      return "Your plan wasn't set up through Stripe, so it can't be changed here. Get in touch and we'll move you across.";
    }
    if (err.message === "no_subscription") {
      return "You're not on a paid plan yet. Pick one below to subscribe.";
    }
    if (err.message === "not_an_upgrade") {
      /* This used to read "That plan isn't above the one you're on. To move down,
         cancel and you'll keep this plan until the end of your term." — which was
         the whole product decision reversed on 2026-08-31. A move down is now
         SCHEDULED for the end of the term and never reaches this branch.
         What still does is a SIDESTEP: same rank, different name, nothing to bill
         and nothing to schedule. Leaving the old sentence here would tell someone
         to cancel a plan they are already on. */
      return "You're already on that plan.";
    }
    return "We can't change your plan from here. Get in touch and we'll sort it.";
  }
  return "Couldn't work out the price. Try again in a moment.";
}

/**
 * Moving UP a plan, for someone who already pays us.
 *
 * Not a checkout, and the difference matters. Checkout sells a NEW subscription,
 * which is exactly the bug this replaces: a per-model subscriber who "bought" Pro
 * ended up with two live subscriptions and paid for both. An upgrade swaps the
 * price on the subscription they already have, so Stripe credits the unused
 * remainder of the old plan against the new one.
 *
 * TWO STEPS, ALWAYS, and the first is not optional. The upgrade charges the
 * prorated difference IMMEDIATELY, so the amount is fetched and shown before
 * anything is committed. Taking an unannounced amount off a saved card is the
 * kind of thing the ACL takes an interest in, and it is also just rude.
 *
 * There is no redirect anywhere in here. The card is already on file, so nobody
 * leaves the page — which is why this cannot reuse useCheckout's
 * location.assign() shape.
 */
export function useUpgrade() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [currency, setCurrency] = useState("AUD");
  /* The proration as Stripe itemises it, and the tax it did or did not calculate.
     Held separately rather than as one `quote` object only because Pricing already
     reads `amountCents` and `currency` by name. */
  const [lines, setLines] = useState<{ description: string; amountCents: number }[]>([]);
  const [taxCents, setTaxCents] = useState<number | null>(null);
  /* A move DOWN takes effect at the end of the paid term instead of charging
     now. Held as an ISO string rather than Stripe's unix seconds because
     formatResetDate — the one place this product formats a date for a person —
     takes ISO, and a second date format is a second thing to get wrong. */
  const [scheduled, setScheduled] = useState(false);
  const [effectiveAtIso, setEffectiveAtIso] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Step 1 — ask what it costs. Commits nothing. */
  async function preview(next: Plan) {
    setBusy(true);
    setError(null);
    setPlan(next);
    try {
      const r = await api.upgradePreview({
        priceId: next.priceId,
        grants: { kind: "tier", tier: next.tier },
      });
      setAmountCents(r.amountDueCents);
      setCurrency(r.currency);
      setLines(r.lines);
      /* `?? null` and not `?? 0`. Stripe omits `tax` when it calculated none,
         which is not the same as calculating zero — and the dialog must render no
         tax line rather than "GST $0.00", which would be a claim about our tax
         treatment that nothing here can stand behind. */
      setTaxCents(r.taxCents ?? null);
      setScheduled(r.scheduled === true);
      setEffectiveAtIso(
        r.effectiveAt ? new Date(r.effectiveAt * 1000).toISOString() : null,
      );
    } catch (e) {
      setPlan(null);
      /*
       * "Try again in a moment" was the only message here, and for the most
       * common refusal it is a lie: a 409 means the server has decided, and
       * retrying produces the identical answer forever.
       *
       * The one that actually happens: a plan granted by support has a
       * SUB#CURRENT row with no Stripe subscription behind it, so there is
       * nothing to swap the price on. Telling that customer to try again sends
       * them round a loop with no exit.
       */
      setError(messageFor(e));
    } finally {
      setBusy(false);
    }
  }

  /** Step 2 — do it. Only ever reachable from a confirmed preview. */
  async function confirm() {
    if (!plan) return;
    setBusy(true);
    setError(null);
    try {
      await api.upgrade({
        priceId: plan.priceId,
        grants: { kind: "tier", tier: plan.tier },
      });
      /*
       * The server answers 202: Stripe has accepted the change, but entitlement
       * lands on the webhook a moment later. Invalidating rather than writing an
       * optimistic tier keeps the UI honest — it shows the new plan when the
       * server says so, and the refetch is what makes that happen quickly.
       */
      await qc.invalidateQueries({ queryKey: entitlementsKey(session?.userId) });
      setPlan(null);
      setAmountCents(null);
    } catch {
      /*
       * Deliberately not "your plan is unchanged". The server cancels any extra
       * subscriptions BEFORE it swaps the price — that ordering is what puts the
       * unused money toward the new plan — so a failure at the swap can leave a
       * customer who held two subscriptions with one of them already ended and a
       * credit sitting on their account. Claiming nothing happened would be a
       * lie in exactly the case where they most need to be told otherwise.
       *
       * Retrying is safe: every Stripe call on that path carries an idempotency
       * key, so a repeat cannot cancel twice or charge twice.
       */
      setError(
        "Couldn't complete the upgrade. You haven't been charged for it. " +
          "Try again, or get in touch if your plan looks wrong.",
      );
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setPlan(null);
    setAmountCents(null);
    setLines([]);
    setTaxCents(null);
    /* Reset with the rest. Left set, a dismissed downgrade would make the NEXT
       dialog — possibly a real upgrade with a real charge — claim nothing is due
       today. */
    setScheduled(false);
    setEffectiveAtIso(null);
    setError(null);
  }

  return {
    plan, amountCents, currency, lines, taxCents, scheduled, effectiveAtIso,
    busy, error, preview, confirm, cancel,
  };
}
